import type {
  Command,
  CommandResult,
  Comment,
  EntityRef,
  KpiSample,
  ProjectEventEnvelope,
  Run,
} from "@minga/core"
import { type BriefHistory, type KpiHistory, type RunsFilter, SyncSnapshot } from "@minga/protocol"
import { Deferred, Effect, Fiber, Schema } from "effect"
import { type AgentPageApi, agentPageOf } from "@/features/agent-page/agent-api"
import { fetchFrom, takeHtmlSnapshot } from "@/lib/early-boot"
import { defined, type KnowledgeApi } from "./knowledge-api"
import { ProjectStore } from "./store"
import {
  browserSocket,
  dispatch,
  makeProjectClient,
  type ProjectClient,
  projectSocketUrl,
  runSync,
} from "./sync"

/**
 * One live connection per open project (W1): a `ProjectStore` plus the socket and the sync loop that feed it.
 * Connections are shared by every view of the project and reference-counted; the last release closes the socket
 * after a short grace period, so switching views (or React StrictMode's double mount) never reconnects.
 */
export interface ProjectConnection {
  readonly key: string
  readonly store: ProjectStore
  /** Send a command; resolves with the server's result (the store already shows it optimistically). */
  readonly dispatch: (command: Command) => Promise<CommandResult>
  /** `ProjectRpcs.Runs`: run history (`project`: all runs newest first; `run`: one run by id). */
  readonly runs: (filter: RunsFilter, limit: number) => Promise<ReadonlyArray<Run>>
  /** `ProjectRpcs.History`: an entity's events, newest first (the item page's activity and revisions). */
  readonly history: (
    ref: EntityRef,
    beforeSeq: number | null,
    limit: number,
  ) => Promise<ReadonlyArray<ProjectEventEnvelope>>
  /** `ProjectRpcs.Thread`: the comments on an entity (notes, updates with health). */
  readonly thread: (target: EntityRef) => Promise<ReadonlyArray<Comment>>
  /** `ProjectRpcs.KpiSeries`: a KPI's samples in a window (goal page sparklines). */
  readonly kpiSeries: (kpiId: string, from: number, to: number) => Promise<ReadonlyArray<KpiSample>>
  /** Decision 0097, `ProjectRpcs.KpiHistory`: a KPI's samples with who and how, and the work around them. */
  readonly kpiHistory: (kpiId: string, from: number, to: number) => Promise<KpiHistory>
  /**
   * `ProjectRpcs.Comments` (decisions 0049, 0051): the newest comments of a kind across the project; `perTarget`: the
   * newest of the kind on every target (`limit` ignored).
   */
  readonly comments: (
    kind: "update" | "decision",
    limit: number,
    perTarget?: boolean,
  ) => Promise<ReadonlyArray<Comment>>
  /** `ProjectRpcs.BriefHistory` (decision 0051): the brief's revisions, newest first. */
  readonly briefHistory: (limit: number) => Promise<BriefHistory>
  /** Wave K (decision 0065): search, past discussions and runs, decided cards. */
  readonly knowledge: KnowledgeApi
  /** Wave C (decision 0071): the agent page's reads (Now, History, a memory's revisions, old handles). */
  readonly agentPage: AgentPageApi
}

interface Entry extends ProjectConnection {
  refs: number
  closeTimer: ReturnType<typeof setTimeout> | null
  readonly fiber: Fiber.Fiber<never, unknown>
}

const CLOSE_GRACE_MS = 5_000
const entries = new Map<string, Entry>()

/** How long the first Subscribe waits for the HTTP snapshot before asking the socket for one (decision 0151). */
export const EARLY_SNAPSHOT_WAIT_MS = 4_000

const SnapshotJson = Schema.toCodecJson(SyncSnapshot)

/**
 * Decision 0151: the project's first sync frames over HTTP (`GET …/snapshot`), on the page's open connection, while
 * the socket's own connection is still being set up (1.4 to 2.6 s measured from Peru, against 0.2 s for a GET). Null
 * when it fails or the server has no such route yet; the socket then sends the snapshot, as before.
 */
export const fetchProjectSnapshot = async (
  workspaceId: string,
  projectId: string,
  fetchFn: typeof fetch = (...args) => fetch(...args),
): Promise<SyncSnapshot | null> => {
  try {
    const response = await fetchFn(
      `/api/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/snapshot`,
      { credentials: "same-origin", headers: { accept: "application/json" } },
    )
    if (!response.ok) return null
    return Schema.decodeUnknownSync(SnapshotJson)(await response.json())
  } catch {
    return null
  }
}

const open = (key: string, url: string, userId: string, workspaceId: string, projectId: string): Entry => {
  const store = new ProjectStore({ userId })
  const ready = Deferred.makeUnsafe<ProjectClient>()
  // decision 0160: the request index.html sent for this project page, if any (it was sent before this code loaded)
  const html = takeHtmlSnapshot(workspaceId, projectId)
  const early = fetchProjectSnapshot(
    workspaceId,
    projectId,
    html ? fetchFrom(html.response) : undefined,
  ).then((frames) => {
    if (frames) store.applyEarly(frames)
  })
  const fiber = Effect.runFork(
    Effect.scoped(
      Effect.gen(function* () {
        const client = yield* makeProjectClient(browserSocket(url))
        Deferred.doneUnsafe(ready, Effect.succeed(client))
        return yield* runSync(client, store, {
          before: Effect.promise(() => early).pipe(Effect.timeout(EARLY_SNAPSHOT_WAIT_MS), Effect.ignore),
        })
      }),
    ),
  )
  return {
    key,
    store,
    refs: 0,
    closeTimer: null,
    fiber,
    dispatch: (command) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(Effect.flatMap((client) => dispatch(client, store, command))),
      ),
    runs: (filter, limit) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(
          Effect.flatMap((client) =>
            // The `project` and `run` filters are P0 additions (spec §4.4); the cast goes once P0 lands.
            client.Runs({ filter: filter as never, beforeSeq: null, limit }),
          ),
        ),
      ),
    history: (ref, beforeSeq, limit) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(
          Effect.flatMap((client) => client.History({ ref, beforeSeq: beforeSeq as never, limit })),
        ),
      ),
    thread: (target) =>
      Effect.runPromise(Deferred.await(ready).pipe(Effect.flatMap((client) => client.Thread({ target })))),
    kpiSeries: (kpiId, from, to) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(
          Effect.flatMap((client) =>
            client.KpiSeries({ kpiId: kpiId as never, from: from as never, to: to as never }),
          ),
        ),
      ),
    kpiHistory: (kpiId, from, to) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(
          Effect.flatMap((client) =>
            client.KpiHistory({ kpiId: kpiId as never, from: from as never, to: to as never }),
          ),
        ),
      ),
    comments: (kind, limit, perTarget) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(
          Effect.flatMap((client) =>
            client.Comments({ kind, limit, ...(perTarget ? { perTarget: true } : {}) }),
          ),
        ),
      ),
    briefHistory: (limit) =>
      Effect.runPromise(
        Deferred.await(ready).pipe(Effect.flatMap((client) => client.BriefHistory({ limit }))),
      ),
    knowledge: knowledgeOf((f) => Effect.runPromise(Deferred.await(ready).pipe(Effect.flatMap(f)))),
    agentPage: agentPageOf((f) => Effect.runPromise(Deferred.await(ready).pipe(Effect.flatMap(f)))),
  }
}

/** The wave K reads over one client (`call` waits for the socket). */
export const knowledgeOf = (
  call: <A>(f: (client: ProjectClient) => Effect.Effect<A, unknown>) => Promise<A>,
): KnowledgeApi => ({
  search: ({ q, kinds, limit }) => call((client) => client.Search(defined({ q, kinds, limit }) as never)),
  lookBack: ({ q, since, until, kinds, limit }) =>
    call((client) => client.LookBack(defined({ q, since, until, kinds, limit }) as never)),
  searchOpened: (searchId, ref) => call((client) => client.SearchOpened({ searchId, ref })),
  decidedIndex: () => call((client) => client.DecidedIndex({})),
  decidedCards: ({ ids, limit, before }) =>
    call((client) => client.DecidedCards(defined({ ids, limit, before }) as never)),
  readAnchor: (anchor) => call((client) => client.ReadAnchor({ anchor })),
  deleteRunTranscript: (runId) => call((client) => client.DeleteRunTranscript({ runId: runId as never })),
})

/** Take a reference to the project's connection, opening it if needed. Call the returned function to release. */
export const acquireProject = (
  workspaceId: string,
  projectId: string,
  userId: string,
): { readonly connection: ProjectConnection; readonly release: () => void } => {
  const key = `${workspaceId}:${projectId}:${userId}`
  let entry = entries.get(key)
  if (!entry) {
    const origin = globalThis.location?.origin ?? "http://localhost"
    entry = open(key, projectSocketUrl(origin, workspaceId, projectId), userId, workspaceId, projectId)
    entries.set(key, entry)
  }
  const e = entry
  e.refs += 1
  if (e.closeTimer) {
    clearTimeout(e.closeTimer)
    e.closeTimer = null
  }
  let released = false
  return {
    connection: e,
    release: () => {
      if (released) return
      released = true
      e.refs -= 1
      if (e.refs > 0) return
      e.closeTimer = setTimeout(() => close(e), CLOSE_GRACE_MS)
    },
  }
}

/** How long a warmed connection is held for the page that is about to use it. */
export const WARM_HOLD_MS = 15_000

/**
 * Open the project's connection ahead of the page (project load speed, 2026-10-17): at boot for a project URL, as soon
 * as the session is known (in parallel with the workspace list, the route guards and the first render), and when a
 * project link is hovered (the router's intent preload). The snapshot then streams in while the rest of the page is
 * still on its way. The reference is held for `holdMs`; the page's own `acquireProject` takes over the same socket, and
 * a warm-up nobody uses closes after the usual grace period.
 */
export const warmProject = (
  workspaceId: string,
  projectId: string,
  userId: string,
  holdMs: number = WARM_HOLD_MS,
): void => {
  const { release } = acquireProject(workspaceId, projectId, userId)
  setTimeout(release, holdMs)
}

/** The project of a project page's path (`/w/<ws>/p/<project>[/…]`), or null. */
export const projectOfPath = (
  pathname: string,
): { readonly workspaceId: string; readonly projectId: string } | null => {
  const m = /^\/w\/([^/]+)\/p\/([^/]+)(?:\/|$)/.exec(pathname)
  if (!m?.[1] || !m[2]) return null
  try {
    return { workspaceId: decodeURIComponent(m[1]), projectId: decodeURIComponent(m[2]) }
  } catch {
    return null
  }
}

/** The connection if it is open (for code that must not open one, like a sign-out). */
export const peekProject = (
  workspaceId: string,
  projectId: string,
  userId: string,
): ProjectConnection | null => entries.get(`${workspaceId}:${projectId}:${userId}`) ?? null

const close = (entry: Entry) => {
  if (entries.get(entry.key) !== entry) return
  entries.delete(entry.key)
  if (entry.closeTimer) clearTimeout(entry.closeTimer)
  Effect.runFork(Fiber.interrupt(entry.fiber))
}

/** Close every project socket and drop every store. Call on sign-out / session loss so the next user starts clean. */
export const closeAllProjects = (): void => {
  for (const entry of [...entries.values()]) close(entry)
}
